import { defineStore } from 'pinia';
import Api from '../api/Api';
import { useAuthStore } from '@/stores/AuthStore.js';

// The maximum number of items to store per account in the search history
const HISTORY_MAX_ITEMS = 5;
// Key under which the items will be stored
const HISTORY_KEY = '__tw_search_history';

/**
 * Returns an array of previous search terms.
 *
 * @returns {Map<string, string[]>}
 */
function retrieveLocalStorageSearchHistory() {
  const value = localStorage.getItem(HISTORY_KEY);
  let parsedValue = null;
  let loadHistoryError = null;

  if (value) {
    try {
      parsedValue = JSON.parse(value);
    } catch (error) {
      loadHistoryError = error;
    }

    if (parsedValue) {
      if (Array.isArray(parsedValue)) {
        return new Map(parsedValue
          .filter((item) => Array.isArray(item) && item.length === 2 && typeof item[0] === 'string' && Array.isArray(item[1]))
          .map((item) => [ item[0], item[1].filter((v) => typeof v === 'string' && v.length > 0) ]));
      }
    }

    if (loadHistoryError) {
      console.error(`Could not load local search history: ${loadHistoryError}`);
    }
  }

  return new Map();
}

/**
 * Returns a list of previous search terms, done by the current user.
 *
 * @returns {string[]}
 */
function retrieveCurrentUserSearchHistory() {
  const authStore = useAuthStore();
  const searchHistory = retrieveLocalStorageSearchHistory();
  return searchHistory.get(authStore.dbUser?.id) ?? [];
}

/**
 * Adds a new record to local search history for user. Returns the updated history.
 *
 * @param {string} query
 * @returns {string[]}
 */
function addQueryToLocalStorageSearchHistory(query) {
  const authStore = useAuthStore();
  const userId = authStore.dbUser.id;
  const history = retrieveLocalStorageSearchHistory();
  let userHistory = history.get(userId) ?? [];

  if (query.length === 0 || userHistory.some((v) => v.toLowerCase() === query.toLowerCase())) {
    return userHistory;
  }

  userHistory = [
    query,
    ...userHistory.slice(0, Math.min(userHistory.length, HISTORY_MAX_ITEMS - 1)),
  ];

  history.set(userId, userHistory);

  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.entries().toArray()));
  } catch (error) {
    console.error(`Could not add record to local search history: ${error}`);
  }

  return userHistory;
}

export const useSearchStore = defineStore('search', {
  state: () => ({
    searchQuery: '',
    searchResults: [],
    searchZone: '',   // It's either 'files' or 'metadata'
    folderSearchResults: [],
    previousSearchQueries: null,
  }),
  getters: {
    getSearchQuery: function (state) {
      return state.searchQuery;
    },
    getSearchZone: function (state) {
      return state.searchZone;
    },
    getSearchResults: function (state) {
      return state.searchResults;
    },
    getAllSearchZones: function () {
      return {
        FILES: 'files',
        METADATA: 'metadata',
      };
    },

    getFolderSearchResults: function (state) {
      return state.folderSearchResults;
    },
    getPreviousSearchQueries: function (state) {
      if (state.previousSearchQueries === null) {
        return retrieveCurrentUserSearchHistory();
      }

      return state.previousSearchQueries;
    },
  },
  actions: {
    _setSearchQuery: function (query) {
      this.searchQuery = query;
    },
    _setSearchZone: function (zone) {
      this.searchZone = zone;
    },
    _setSearchResults: function (results) {
      this.searchResults = results;
    },
    _updateSearchResultPod(pod) {
      for (let i = 0; i < this.searchResults.length; i++) {
        if (Array.isArray(this.searchResults[i].pods)) {
          for (let j = 0; j < this.searchResults[i].pods.length; j++) {
            if (this.searchResults[i].pods[j].id === pod.id) {
              Object.assign(this.searchResults[i].pods[j], pod);
            }
          }
        }
      }
    },

    _setFolderSearchResults(results) {
      // Uncomment if not "WTF??"
      /*
      const updatedData = results.filter(item => item.id !== 648).map(item => {
        if (item.parentid === 648) {
          item.parentid = null;
          item.parent = null;
        }
        return item;
      });
       */

      this.folderSearchResults = results;
    },

    search: async function (payload) {
      this._setSearchQuery(payload.searchStr);
      this._setSearchZone(payload.searchZone);

      const result = await Api.search.searchNodesAndPods(payload);
      this._setSearchResults(result.data);
      this.previousSearchQueries = addQueryToLocalStorageSearchHistory(payload.searchStr);
      return result.data;
    },
    searchInFolders: async function (payload) {
      const result = await Api.search.searchNodesAndPods(payload);
      this.previousSearchQueries = addQueryToLocalStorageSearchHistory(payload.searchStr);
      this._setFolderSearchResults(result.data);
    },

    updateSearchResultPod(pod) {
      this._updateSearchResultPod(pod);
    },
    resetFolderSearchResults: function () {
      this._setFolderSearchResults([]);
    },
    refreshCurrentUserSearchHistory: function () {
      this.previousSearchQueries = retrieveCurrentUserSearchHistory();
    }
  },
});
